import {
  FieldValues,
  Path,
  PathValue,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  EventCreateEditFormValues,
  EventLocationFormValues,
  getEventLocationFormValues,
  isEmptyLocation,
} from "../EventCreateEditModal/useEventCreateEditForm";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import { useEventCreateEditContext } from "../EventCreateEditContext/useEventCreateEditContext";
import { LocationListData } from "apps/events/types/LocationListData";
import { getAddressString, getMultilingualString } from "shared/utils";
import { EnumEventLocationLocationInfoType } from "gql/generated";
import { Combobox, useFilter } from "@ttc3k/trekker";
import _ from "lodash";

export type UseEventVenueProps<T extends FieldValues> = {
  formFieldRoot: Path<T>;
  isEventOrganizer: boolean;
  setIsEmpty?: Dispatch<SetStateAction<boolean>>;
};

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_CHARS = 2;

const getVenueDisplayName = (venue: EventLocationFormValues["venue"]) => {
  if (typeof venue === "string") return venue.trim();
  return getMultilingualString(venue ?? {}).trim();
};

export const useEventVenue = <T extends FieldValues>({
  formFieldRoot,
  isEventOrganizer,
  setIsEmpty,
}: UseEventVenueProps<T>) => {
  const {
    allLocations,
    searchResults,
    searchOperators,
    prefetchOperatorsByEntityIds,
    useOperatorPreload,
    limitHostToOperatorEntityId,
    event,
    defaultOperatorEntityIds,
    organizerEntityIds,
    isOperatorUser,
  } = useEventCreateEditContext();

  const useSearchMode = !(useOperatorPreload && isEventOrganizer);

  /**
   * Operator users picking an additional location (i.e. from Home) browse
   * their own listings first and only hit the database once they type. Org
   * users in the Events app and public submitters keep the pure search
   * behaviour, since they have no listings of their own to browse.
   */
  const browsePreloadedFirst = isOperatorUser && !isEventOrganizer;

  const { control, formState, getValues, register, setValue } =
    useFormContext<EventCreateEditFormValues>();
  const locationType = useWatch({ control, name: "locationType" });

  register("access");
  register("access.operator");
  register("access.organizations");

  const locationCacheRef = useRef<Map<string, LocationListData>>(new Map());
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const prefetchedEntityIdsRef = useRef<Set<string>>(new Set());

  const allLocationsFilteredForHostLimiting = useMemo(
    () =>
      limitHostToOperatorEntityId && isEventOrganizer
        ? allLocations.filter(
            location => location.value === limitHostToOperatorEntityId,
          )
        : allLocations,
    [allLocations, isEventOrganizer, limitHostToOperatorEntityId],
  );

  const [lastSearch, setLastSearch] = useState<string>("");
  const { contains } = useFilter({ sensitivity: "base" });

  const comboboxItems = useMemo(() => {
    if (!useSearchMode) {
      return allLocationsFilteredForHostLimiting;
    }

    const query = lastSearch.trim();
    // Locally-held listings have to be matched client-side; results from
    // searchOperators were already matched by the server, so they are never
    // re-filtered here.
    const matchesQuery = (location: LocationListData) =>
      !browsePreloadedFirst || query.length === 0
        ? true
        : contains(location.label, query);

    const byValue = new Map<string, LocationListData>();

    allLocationsFilteredForHostLimiting
      .filter(matchesQuery)
      .forEach(location => {
        byValue.set(location.value, location);
      });
    searchResults.forEach(location => {
      byValue.set(location.value, location);
    });
    locationCacheRef.current.forEach((location, value) => {
      if (!byValue.has(value) && matchesQuery(location)) {
        byValue.set(value, location);
      }
    });

    return _.sortBy(Array.from(byValue.values()), "label");
  }, [
    useSearchMode,
    browsePreloadedFirst,
    allLocationsFilteredForHostLimiting,
    searchResults,
    lastSearch,
    contains,
  ]);

  const findLocation = useCallback(
    (value: string) =>
      comboboxItems.find(location => location.value === value) ??
      locationCacheRef.current.get(value) ??
      allLocationsFilteredForHostLimiting.find(location => location.value === value),
    [comboboxItems, allLocationsFilteredForHostLimiting],
  );

  const [locationMode, setLocationMode] = useState<"db" | "new">("db");

  const formLocationValue = useWatch({
    control,
    name: formFieldRoot as PathValue<T, Path<T>>,
  }) as EventLocationFormValues | undefined;
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");

  const locationEntityId = formLocationValue?.locationInfo?._entityId ?? "";

  useEffect(() => {
    prefetchedEntityIdsRef.current = new Set();
    setLastSearch("");
  }, [formFieldRoot]);

  useEffect(() => {
    if (
      locationEntityId &&
      !findLocation(locationEntityId) &&
      !prefetchedEntityIdsRef.current.has(locationEntityId)
    ) {
      prefetchedEntityIdsRef.current.add(locationEntityId);
      prefetchOperatorsByEntityIds([locationEntityId]);
    }
  }, [locationEntityId, findLocation, prefetchOperatorsByEntityIds]);

  useEffect(() => {
    const location = locationEntityId ? findLocation(locationEntityId) : undefined;

    if (location) {
      setSelectedLocationId(locationEntityId);
      setLocationMode("db");
      locationCacheRef.current.set(location.value, location);
      return;
    }

    if (!formLocationValue || isEmptyLocation(formLocationValue)) {
      setSelectedLocationId("");
      setLocationMode("db");
      return;
    }

    if (locationEntityId) {
      // Linked listing — stay in db mode while prefetch resolves
      setLocationMode("db");
      return;
    }

    setSelectedLocationId("");
    setLocationMode("new");
  }, [
    formFieldRoot,
    formLocationValue,
    locationEntityId,
    findLocation,
  ]);

  const comboboxValue = useMemo(() => {
    if (locationMode !== "db") return [];
    const resolvedId = locationEntityId || selectedLocationId;
    if (!resolvedId || !findLocation(resolvedId)) return [];
    return [resolvedId];
  }, [locationMode, locationEntityId, selectedLocationId, findLocation]);

  const venueDisplayName = useMemo(
    () => getVenueDisplayName(formLocationValue?.venue),
    [formLocationValue?.venue],
  );

  const hasExistingLocation = useMemo(
    () => !!formLocationValue && !isEmptyLocation(formLocationValue),
    [formLocationValue],
  );

  const populateLocationFields = useCallback(
    (
      location: LocationListData,
      fieldRoot: "eventOrganizer" | `eventLocations.${number}`,
    ) => {
      locationCacheRef.current.set(location.value, location);
      setSelectedLocationId(location.data.locationInfo._entityId ?? "");
      const formData = getEventLocationFormValues(location.data);

      setValue(
        `${fieldRoot}.contact.fullAddress`,
        getAddressString({
          line1: formData.contact.address?.line1,
          line2: formData.contact.address?.line2,
          city: formData.contact.address?.city,
          subdivision: formData.contact.address?.subdivision,
          postalCode: formData.contact.address?.postalCode,
          country: formData.contact.address?.country,
        }),
        { shouldDirty: true },
      );
      setValue(`${fieldRoot}.contact.address`, formData.contact.address ?? {}, {
        shouldDirty: true,
      });
      setValue(`${fieldRoot}.contact.email`, formData.contact.email ?? "", {
        shouldDirty: true,
      });
      setValue(`${fieldRoot}.contact.phone`, formData.contact.phone ?? "", {
        shouldDirty: true,
      });
      setValue(
        `${fieldRoot}.contact.links.website`,
        formData.contact.links.website ?? "",
        { shouldDirty: true },
      );
      setValue(`${fieldRoot}.venue`, formData.venue, {
        shouldDirty: true,
        ...(fieldRoot === "eventOrganizer" ? { shouldValidate: true } : {}),
      });
      setValue(`${fieldRoot}.locationInfo`, formData.locationInfo, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  // Host selection updates only access.operator. access.organizations is
  // never computed/sent by the client — connected-organization targeting is
  // derived server-side from the selected host's active enrollments on
  // submit (see eventUpdate.ts), and the "Submit to" preview derives its
  // recipient list independently (see useEventPreview.ts).
  const updateEventAccess = useCallback(
    (location?: LocationListData) => {
      const currentAccess = getValues("access");

      if (
        location &&
        location.data.locationInfo.type ===
          EnumEventLocationLocationInfoType.Operator
      ) {
        if (
          currentAccess.operator?._entityId !==
          location.data.locationInfo._entityId
        ) {
          setValue(
            "access.operator._entityId",
            location.data.locationInfo._entityId,
            { shouldDirty: true },
          );
        }
      } else if (
        location &&
        location.data.locationInfo.type ===
          EnumEventLocationLocationInfoType.Organization
      ) {
        setValue("access.operator", undefined, { shouldDirty: true });
      } else if (!location) {
        setValue("access.operator", undefined, { shouldDirty: true });
      }
    },
    [getValues, setValue],
  );

  const handleSwitchEventOrganizerMode = useCallback(
    (mode: "db" | "new") => {
      setLocationMode(mode);
      setValue("eventOrganizer", getEventLocationFormValues({ venue: {} }), {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const handleSwitchEventLocationsMode = useCallback(
    (mode: "db" | "new", index: number) => {
      setLocationMode(mode);
      setValue(
        `eventLocations.${index}`,
        getEventLocationFormValues({ venue: {} }),
        { shouldDirty: true },
      );
    },
    [setValue],
  );

  const handleEventOrganizerComboboxValueChange = useCallback(
    (value: string) => {
      if (value === "NEW") {
        handleSwitchEventOrganizerMode("new");
        setSelectedLocationId("");
        setValue("eventOrganizer.venue", lastSearch, { shouldDirty: true });
        return;
      }

      setLocationMode("db");
      const location = findLocation(value);
      if (location) {
        populateLocationFields(location, "eventOrganizer");
        updateEventAccess(location);
      }
    },
    [
      findLocation,
      lastSearch,
      populateLocationFields,
      setValue,
      handleSwitchEventOrganizerMode,
      updateEventAccess,
    ],
  );

  const handleEventLocationsComboboxValueChange = useCallback(
    (value: string, index: number) => {
      if (value === "NEW") {
        handleSwitchEventLocationsMode("new", index);
        setSelectedLocationId("");
        setValue(`eventLocations.${index}.venue`, lastSearch, {
          shouldDirty: true,
        });
        return;
      }

      setLocationMode("db");
      const location = findLocation(value);
      if (location) {
        populateLocationFields(location, `eventLocations.${index}`);
      }
    },
    [
      findLocation,
      lastSearch,
      populateLocationFields,
      setValue,
      handleSwitchEventLocationsMode,
    ],
  );

  const { collection, filter, set } = Combobox.useCollection({
    initialItems: comboboxItems,
    filter: useSearchMode ? () => true : contains,
  });

  const handleComboboxInputValueChange = useCallback(
    (search: string) => {
      setLastSearch(search);

      if (useSearchMode) {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
        if (search.trim().length < MIN_SEARCH_CHARS) {
          searchOperators(search);
          return;
        }
        searchDebounceRef.current = setTimeout(() => {
          searchOperators(search);
        }, SEARCH_DEBOUNCE_MS);
        return;
      }

      filter(search);
    },
    [
      useSearchMode,
      searchOperators,
      filter,
    ],
  );

  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const handleComboboxOpenChange = useCallback(
    (open: boolean) => {
      if (open && !useSearchMode) {
        set(allLocationsFilteredForHostLimiting);
      }
      setIsComboboxOpen(open);
    },
    [useSearchMode, allLocationsFilteredForHostLimiting, set],
  );

  useEffect(() => {
    set(comboboxItems);
  }, [comboboxItems, set]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const hasAutoSelected = useRef(false);
  useEffect(() => {
    if (
      isEventOrganizer &&
      !event?._id &&
      !hasAutoSelected.current &&
      allLocationsFilteredForHostLimiting.length > 0 &&
      (!formLocationValue || isEmptyLocation(formLocationValue))
    ) {
      const soleConnectedListing =
        organizerEntityIds?.length === 1
          ? allLocationsFilteredForHostLimiting.find(
              loc => loc.value === organizerEntityIds[0],
            )
          : undefined;
      const userOperator = limitHostToOperatorEntityId
        ? allLocationsFilteredForHostLimiting.find(
            loc => loc.value === limitHostToOperatorEntityId,
          )
        : (allLocationsFilteredForHostLimiting.find(loc =>
            defaultOperatorEntityIds.includes(loc.value),
          ) ?? soleConnectedListing);
      if (userOperator) {
        hasAutoSelected.current = true;
        handleEventOrganizerComboboxValueChange(userOperator.value);
      }
    }
  }, [
    isEventOrganizer,
    event?._id,
    allLocationsFilteredForHostLimiting,
    formLocationValue,
    defaultOperatorEntityIds,
    organizerEntityIds,
    limitHostToOperatorEntityId,
    handleEventOrganizerComboboxValueChange,
  ]);

  const handleCustomLocationNameBlur = () => {
    updateEventAccess();
  };

  const locationBeingUpdated = useWatch({
    control,
    name: formFieldRoot as PathValue<T, Path<T>>,
  });

  useEffect(() => {
    if (!setIsEmpty) return;

    setIsEmpty(
      locationBeingUpdated
        ? isEmptyLocation(locationBeingUpdated as EventLocationFormValues)
        : true,
    );
  }, [locationBeingUpdated, setIsEmpty]);

  return {
    allLocations,
    locationType,
    filteredLocationListCollection: collection,
    formState,
    locationMode,
    selectedLocationId,
    comboboxValue,
    comboboxInputValue: lastSearch || venueDisplayName,
    hasExistingLocation,
    isComboboxOpen,
    lastSearch,
    useSearchMode,
    browsePreloadedFirst,
    handleEventOrganizerComboboxValueChange,
    handleEventLocationsComboboxValueChange,
    handleComboboxInputValueChange,
    handleSwitchEventOrganizerMode,
    handleSwitchEventLocationsMode,
    handleCustomLocationNameBlur,
    handleComboboxOpenChange,
  };
};
